import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  fabricantesBreadcrumbItems,
  FabricanteTableType,
} from "data/fabricantes";
import FabricanteTable, {
  fabricanteTableColumns,
} from "components/tables/FabricanteTable";
import { usePermissionModal } from "contexts/PermissionModalContext";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";

const fetchFabricantes = async () => {
  const { data } = await axios.get("/api/fabricante");
  return data;
};

const Fabricantes = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const dataQuery = useQuery<FabricanteTableType[]>({
    queryKey: ["fabricantes"],
    queryFn: fetchFabricantes,
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["fabricantes"],
      defaultMessage:
        "Você não tem permissão para consultar ou visualizar fabricantes.",
    }),
  });

  const table = useReactTable({
    data: dataQuery.data ?? [],
    columns: fabricanteTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (dataQuery.error) {
    // Utilizamos o componente AccessDeniedOverlay para exibir uma interface de erro mais elegante
    // A mensagem específica para erros 403 já foi mostrada pelo getQueryRetryHandler
    return (
      <div>
        <PageBreadcrumb items={fabricantesBreadcrumbItems} />
        <div className="mb-9">
          <h2 className="mb-5">Fabricantes</h2>

          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar fabricante"
                onChange={() => {}}
                disabled
              />
              <div className="ms-xxl-auto">
                <Button
                  variant="primary"
                  as={Link}
                  to="/cadastros/fabricantes/novo"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar fabricantes.">
            <div
              className="position-relative"
              style={{
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descrição</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <tr key={i}>
                            <td>{i + 100}</td>
                            <td>Fabricante Exemplo</td>
                            <td>-</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </AccessDeniedOverlay>
        </div>
      </div>
    );
  }

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={fabricantesBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Fabricantes</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar fabricante"
                onChange={handleSearchInputChange}
              />
              <div className="ms-xxl-auto">
                <Button
                  variant="primary"
                  as={Link}
                  to="/cadastros/fabricantes/novo"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <FabricanteTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Fabricantes;
